"use client";

import React, { useEffect, useState, createContext, useContext } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { setAuthenticatedUser, resetUser } from '@/store/userSlice';
import { fetchAlerts } from '@/store/alertsSlice';
import { securityServiceLogger as LOGGER } from '@/lib/logger/logger';
import { INTERNAL, OPTUMID } from '@/types/User';
import { AppDispatch } from '@/store';
import { LOGGED_OUT_KEY } from '@/components/shared/constants';
/**
 * Context for sharing UI state related to authentication
 * @description Provides access to showAppFrame state throughout the app
 */
export const AuthUIContext = createContext<{ showAppFrame: boolean }>({ showAppFrame: true });

/**
 * Hook to easily access auth UI state
 * @returns The current auth UI state including showAppFrame
 */
export const useAuthUI = () => useContext(AuthUIContext);

/**
 * AuthProvider Component
 *
 * @component
 * @description Manages authentication state throughout the application by:
 * - Validating user sessions on initial load and route changes
 * - Handling different auth flows for internal and external users
 * - Managing UI states during authentication
 * - Enforcing route protections for secure pages
 * - Redirecting users based on authentication state
 *
 * Authentication Flow:
 * 1. On mount or route change, check if we're on a secure route with existing user data
 * 2. If not, attempt to validate the user via API
 * 3. On successful validation, store user data and set appropriate UI flags
 * 4. On failed validation, redirect from secure routes to login
 *
 * Special Behaviors:
 * - Internal users: App frame is hidden (for embedding in internal systems)
 * - External users: Post-login alerts are fetched
 * - Development mode: Authenticated users aren't auto-redirected from homepage
 * - Production mode: Authenticated users at root path are sent to dashboard
 *
 * @example
 * // Wrap your application with AuthProvider
 * <AuthProvider>
 *   <YourApp />
 * </AuthProvider>
 */
type AuthProviderProps = {
  /** React children to be rendered within the authenticated context */
  children: React.ReactNode;
};

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname();

  const dispatch: AppDispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user) as { isAuthenticated: boolean; type: string } | null | undefined;
  const [isInitialized, setIsInitialized] = useState(false);
  const [showAppFrame, setShowAppFrame] = useState(true);
  const [fetchInProgress, setFetchInProgress] = useState(false);

  /**
   * Checks if the authentication cookie exists in the browser
   *
   * @function checkAuthCookie
   * @returns {boolean} True if pixe_token cookie exists, false otherwise
   */
  const checkAuthCookie = (): boolean => {
    return document.cookie.split(';').some(item =>
      item.trim().startsWith('pixe_token='));
  };

  useEffect(() => {
    /**
     * Validates the current user session
     *
     * @async
     * @function validateUser
     * @description Attempts to validate user credentials and sets appropriate state
     * based on the result. Handles redirects for secure routes and manages UI state.
     */
    const validateUser = async () => {
      // Prevent concurrent fetches to avoid race conditions
      if (fetchInProgress) {
        return;
      }

      try {
        // Check 1 - if user is logged out skip validation
        let loggedOut = false;
        try {
         loggedOut = localStorage.getItem(LOGGED_OUT_KEY) === "true";
         if (loggedOut) {
          // Fetch public alerts for unauthenticated users
          dispatch(fetchAlerts({ filterDisplay: 'preLogin' }));
          // Don't validate, just show public UI
          dispatch(resetUser());
          setIsInitialized(true);
          //push the route to public home if on secure route
         //  loggedOut ?  router.push('/logout') : '';
            localStorage.removeItem(LOGGED_OUT_KEY);
          return;
        }
        } catch (error) {
          LOGGER.error(error, 'Error reading logged out state from localStorage');
        }


        // Check 2 for authentication cookie
        const hasAuthCookie = checkAuthCookie();

        // If cookie doesn't exist but user is authenticated in state, reset user and redirect
        if (!hasAuthCookie && user?.isAuthenticated) {
          LOGGER.debug('pixe_token cookie not found, resetting user state');
          dispatch(resetUser());

          // Redirect to home page if not already there
          if (pathname !== '/') {
            router.replace('/');
            return;
          }
        }

        const isSecureRoute = pathname?.startsWith('/secure');

        // If we're on a secure route and already have user data, no need to validate
        if (user) {
          if (user.type === INTERNAL) {
            setShowAppFrame(false);
          } else if (user.type === OPTUMID) {
            // Fetch alerts for external users
            dispatch(fetchAlerts({ filterDisplay: 'postLogin' }));
          }

          // Small delay to prevent flash of content
          setTimeout(() => setIsInitialized(true), 20);
          return;
        }

        // Prevent revalidation if user is already authenticated
        // This fixes the infinite loop on homepage
        const { isAuthenticated, type } = user || { isAuthenticated: false, type: null };
        if (isAuthenticated) {
          if (type === OPTUMID) {
            // Call this only once when needed
            if (!isInitialized) {
              dispatch(fetchAlerts({ filterDisplay: 'postLogin' }));
            }
          }
          // Still need to mark as initialized
          if (!isInitialized) {
            setTimeout(() => setIsInitialized(true), 20);
          }
          return;
        }

        // Otherwise try to validate user
        try {
          LOGGER.debug('Validating user via /papi/secure/validate');
          setFetchInProgress(true);

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 20000); // 30s timeout

          try {
            const response = await fetch('/papi/secure/validate', {
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              },
              signal: controller.signal
            });

            clearTimeout(timeoutId);
            setFetchInProgress(false);

            if (response.ok) {
              const userData = await response.json();
              LOGGER.debug('User validated successfully:', userData);

              // Store user in Redux
              dispatch(setAuthenticatedUser(userData));

              // Handle UI based on user type
              if (userData.type === INTERNAL) {
                setShowAppFrame(false);
              } else if (userData.type === OPTUMID) {
                // Fetch alerts for authenticated external users
                dispatch(fetchAlerts({ filterDisplay: 'postLogin' }));
              }

              // Handle redirection for authenticated users on homepage
              if (pathname === '/') {
                // Check if we're in development environment
                const isDevelopment =
                  typeof window !== 'undefined' &&
                  window.location.hostname === 'localhost';

                // Only redirect in production environments
                if (!isDevelopment) {
                  router.replace('/secure/dashboard');
                }
              }
            } else {
              LOGGER.debug('User validation failed, fetching preLogin alerts');

              // Fetch public alerts for unauthenticated users
              dispatch(fetchAlerts({ filterDisplay: 'preLogin' }));

              // If on a secure route, redirect to login
              if (isSecureRoute) {
                router.replace('/login');
                return;
              }
            }
          } catch (fetchError) {
            clearTimeout(timeoutId);
            setFetchInProgress(false);

            if (fetchError instanceof Error && fetchError.name === 'AbortError') {
              LOGGER.error('Request timed out while validating user');
            } else {
              LOGGER.error(fetchError, 'Network error while validating user ');
            }

            // Check if we're in development mode
            const isDevelopment =
              typeof window !== 'undefined' &&
              window.location.hostname === 'localhost';

            if (isDevelopment) {
              LOGGER.warn('Using development fallback for authentication failures');
              // Continue loading the app in development even without auth
              setTimeout(() => setIsInitialized(true), 20);
              return;
            }

            // Fetch public alerts for error case
            dispatch(fetchAlerts({ filterDisplay: 'preLogin' }));

            // If on a secure route, redirect to login
            if (isSecureRoute) {
              router.replace('/login');
              return;
            }
          }
        } catch (error) {
          setFetchInProgress(false);
          LOGGER.error(error, 'Error during user validation');

          // Fetch public alerts for error case
          dispatch(fetchAlerts({ filterDisplay: 'preLogin' }));

          // If on a secure route, redirect to login
          if (isSecureRoute) {
            router.replace('/login');
            return;
          }
        }

        // Delay initialization slightly to prevent flash
        setTimeout(() => setIsInitialized(true), 20);
      } catch (error) {
        LOGGER.error(error, 'Fatal error in AuthProvider');
        setIsInitialized(true);
      }
    };

    validateUser();
  }, [pathname]);

  return (
    <AuthUIContext.Provider value={{ showAppFrame }}>
      <div style={{
          opacity: isInitialized ? 1 : 0,
        transition: 'opacity 300ms ease-out'
      }}>
        {isInitialized ? children : (
          <div className="simple-loading" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100%'
          }}>
            Loading...
          </div>
        )}
      </div>
    </AuthUIContext.Provider>
  );
};

export default AuthProvider;
